import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BadgeCheck,
  Bot,
  Calculator,
  FileSearch,
  Globe2,
  Handshake,
  PackageSearch,
  Search,
  Store,
  Wrench,
} from "lucide-react";
import { DashboardShell, PageHeading, useAggregates } from "@/components/DashboardShell";

export const Route = createFileRoute("/services")({
  head: () => ({ meta: [{ title: "Services — Ready4Trade" }] }),
  component: ServicesPage,
});

const WEB = [
  {
    to: "/gsp-plus",
    icon: BadgeCheck,
    title: "GSP+ advantage",
    text: "Uzbekistan's EU preference: pipeline value, withdrawal exposure, REX readiness and statement-on-origin wording.",
  },
  {
    to: "/hs",
    icon: FileSearch,
    title: "AI HS finder",
    text: "Gemini-assisted HS6 screening with official HS 2022 validation and buyer-lead evidence.",
  },
  {
    to: "/leads",
    icon: Search,
    title: "International buying enquiries",
    text: "Search the cleaned Ready4Trade buyer-lead dataset by product, HS code, market and commercial details.",
  },
  {
    to: "/markets",
    icon: Globe2,
    title: "Market intelligence",
    text: "Compare destination demand, RCA-aligned opportunities and product-market signals.",
  },
  {
    to: "/tools",
    icon: Calculator,
    title: "Trade tools",
    text: "Incoterms advisor, landed cost, export pricing, invoice builder, logistics and payments.",
  },
  {
    to: "/eu",
    icon: Globe2,
    title: "EU Export Assistant",
    text: "Use Ready4Trade context with current official European Commission news and authoritative EU source links.",
  },
] as const;

const TELEGRAM = [
  {
    icon: Handshake,
    title: "International buyer profile directory",
    text: "Company/member profile service currently uses the Telegram bot's private member-profile dataset.",
  },
  {
    icon: Store,
    title: "Local supplier discovery",
    text: "Search Uzbek suppliers and historical offers from the Telegram bot's supplier datasets.",
  },
  {
    icon: PackageSearch,
    title: "Business / product registration",
    text: "Persistent registration remains on the bot until the web version receives a secure database backend.",
  },
  {
    icon: Wrench,
    title: "Foreign-trade contract support",
    text: "The established contract workflow remains available in Telegram while web document modules are expanded.",
  },
];

function ServicesPage() {
  const agg = useAggregates();
  return (
    <DashboardShell meta={agg.data?.meta}>
      <PageHeading
        title="Ready4Trade services"
        subtitle="One export journey across web analytics, AI screening, deal calculators and the existing Telegram service layer."
        badge="Web + Telegram"
      />

      <section>
        <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Available on the web
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {WEB.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="surface-panel group p-5 transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
            >
              <item.icon className="h-5 w-5 text-primary" />
              <h2 className="mt-3 font-display text-base group-hover:text-primary">{item.title}</h2>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.text}</p>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Bot className="h-4 w-4" /> Connected Telegram services
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {TELEGRAM.map((item) => (
            <a
              key={item.title}
              href="https://t.me/Ready4Trade_bot"
              target="_blank"
              rel="noreferrer"
              className="surface-panel p-5 transition hover:border-primary/30"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-secondary p-2">
                  <item.icon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h2 className="font-display text-base">{item.title}</h2>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.text}</p>
                  <span className="mt-3 inline-block text-xs font-semibold text-primary">
                    Open @Ready4Trade_bot ↗
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
        <p className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-xs leading-relaxed text-blue-900">
          These links are intentional: Ready4Trade does not expose your private supplier/member
          datasets or pretend that a static browser form is a persistent database. They can be
          migrated to shared Cloudflare storage/API services in a later backend phase.
        </p>
      </section>
    </DashboardShell>
  );
}
